import { useCallback, useEffect, useRef, useState } from 'react'
import { exploreLeagues, joinLeagueById } from '../api/leagues'
import { ApiError } from '../api/client'
import type { ExploreLeague, League } from '../types'

/** How long typing has to stop before a search goes out. */
export const SEARCH_DEBOUNCE_MS = 250
/** Matches the server's page size — what one "Load more" adds. */
export const EXPLORE_PAGE_SIZE = 30

export interface UseExploreLeaguesOptions {
  onToast: (message: string) => void
  /** Patches the user's own league list after an open-policy join, so Leagues shows it at once. */
  onJoined: (league: League) => void
  /** Pushes the newly joined league's detail screen, the same as joining by code does. */
  onOpenLeague: (leagueId: string) => void
}

export interface ExploreLeaguesController {
  /** Whether the Explore screen is the open route. */
  isOpen: boolean
  open: () => void
  close: () => void
  query: string
  setQuery: (query: string) => void
  results: ExploreLeague[]
  isLoading: boolean
  /** True while the current page is full, so there may be another one behind it. */
  hasMore: boolean
  loadMore: () => void
  /** Join (policy `open`) or ask to join (policy `request`) — the server decides which. */
  join: (league: ExploreLeague) => Promise<void>
}

/**
 * The Explore directory's client state.
 *
 * Two things here are not incidental:
 *
 * - **Last query wins.** Every search carries a sequence number and a slower earlier response is
 *   dropped on arrival. Without it, typing fast enough for two requests to overlap can leave the
 *   list showing results for a query the user has already moved past.
 * - **Debounced, not per-keystroke.** A search fires once typing pauses, so a five-letter name is
 *   one request rather than five.
 */
export function useExploreLeagues({
  onToast,
  onJoined,
  onOpenLeague,
}: UseExploreLeaguesOptions): ExploreLeaguesController {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ExploreLeague[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  // Bumped per search; a response whose number is stale by the time it lands is thrown away.
  const requestSeq = useRef(0)
  const latestApplied = useRef(0)

  const runSearch = useCallback(
    async (nextQuery: string) => {
      const seq = ++requestSeq.current
      setIsLoading(true)
      try {
        const rows = await exploreLeagues(nextQuery)
        if (seq < latestApplied.current) return
        latestApplied.current = seq
        setResults(rows)
        setHasMore(rows.length === EXPLORE_PAGE_SIZE)
      } catch {
        if (seq < latestApplied.current) return
        latestApplied.current = seq
        setResults([])
        setHasMore(false)
        onToast("Couldn't load leagues. Please try again.")
      } finally {
        if (seq === requestSeq.current) setIsLoading(false)
      }
    },
    [onToast]
  )

  // One effect covers both the screen opening (empty query, whole directory) and every later
  // keystroke; the cleanup is what makes it a debounce rather than a request per character.
  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => void runSearch(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isOpen, query, runSearch])

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    query,
    setQuery,
    results,
    isLoading,
    hasMore,

    loadMore: () => {
      const offset = results.length
      void (async () => {
        try {
          const rows = await exploreLeagues(query, offset)
          // Appends rather than replaces, and guards against a page that landed after the query
          // changed underneath it (which would splice results for two different searches).
          setResults((prev) => (prev.length === offset ? [...prev, ...rows] : prev))
          setHasMore(rows.length === EXPLORE_PAGE_SIZE)
        } catch {
          onToast("Couldn't load more leagues. Please try again.")
        }
      })()
    },

    join: async (league) => {
      try {
        const result = await joinLeagueById(league.id)
        if ('status' in result) {
          setResults((prev) =>
            prev.map((row) => (row.id === league.id ? { ...row, viewerState: 'requested' } : row))
          )
          onToast(`Asked to join ${league.name}.`)
          return
        }
        setResults((prev) =>
          prev.map((row) => (row.id === league.id ? { ...row, viewerState: 'member' } : row))
        )
        onJoined(result)
        setIsOpen(false)
        onOpenLeague(result.id)
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          onToast('You have already asked to join that league.')
        } else if (err instanceof ApiError && err.status === 403) {
          onToast('That league is invite only.')
        } else {
          onToast("Couldn't join that league. Please try again.")
        }
      }
    },
  }
}
